import { useState, useRef, useCallback, useEffect } from 'react'
import { HiChevronUp, HiChevronDown } from 'react-icons/hi'

const clamp = (val, min, max) => Math.max(min, Math.min(max, val))
const pad = (n) => String(n).padStart(2, '0')

/* ─── Drum-style column for mobile (Samsung alarm style) ─── */
const DRUM_H = 52
const DRUM_VISIBLE = 3
const DRUM_CENTER = 1

const lerp = (a, b, t) => a + (b - a) * t

const getOpacity = (dist) => {
    if (dist <= 0) return 1
    if (dist >= 2) return 0.05
    if (dist < 1) return lerp(1, 0.3, dist)
    return lerp(0.3, 0.05, dist - 1)
}

const getFontSize = (dist) => {
    if (dist <= 0) return 2
    if (dist >= 2) return 1
    if (dist < 1) return lerp(2, 1.25, dist)
    return lerp(1.25, 1, dist - 1)
}

const DrumColumn = ({ value, max, onChange }) => {
    const containerRef = useRef(null)
    const itemsRef = useRef(null)
    const editInputRef = useRef(null)
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState('')
    const editingRef = useRef(false)
    const count = max + 1 // 24 for hours, 60 for minutes

    useEffect(() => { editingRef.current = editing }, [editing])

    // Position value v in the middle copy (index = count + v) at the viewport center
    const tyForVal = (v) => (DRUM_CENTER - (count + v)) * DRUM_H

    const s = useRef({
        dragging: false, startY: 0, startTy: 0,
        lastY: 0, lastT: 0, vel: 0,
        ty: tyForVal(value),
        prevCenter: count + value,
        animFrame: null,
    })
    const valueRef = useRef(value)
    const onChangeRef = useRef(onChange)
    useEffect(() => { valueRef.current = value }, [value])
    useEffect(() => { onChangeRef.current = onChange }, [onChange])

    const commitEdit = () => {
        const num = parseInt(draft, 10)
        if (!isNaN(num)) {
            const clamped = clamp(num, 0, max)
            if (clamped !== valueRef.current) onChangeRef.current(clamped)
        }
        setEditing(false)
    }

    // Update opacity & fontSize of items near a visual center (smooth interpolation)
    const styleItems = useCallback((rawCenter) => {
        if (!itemsRef.current) return
        const prev = s.current.prevCenter
        const total = count * 3
        const lo = Math.max(0, Math.min(Math.floor(prev), Math.floor(rawCenter)) - 4)
        const hi = Math.min(total - 1, Math.max(Math.ceil(prev), Math.ceil(rawCenter)) + 4)
        const children = itemsRef.current.children
        for (let i = lo; i <= hi; i++) {
            const span = children[i]?.firstChild
            if (!span) continue
            const dist = Math.abs(i - rawCenter)
            span.style.opacity = getOpacity(dist)
            span.style.fontSize = getFontSize(dist) + 'rem'
        }
        s.current.prevCenter = rawCenter
    }, [count])

    // Sync position when value changes externally (not during drag)
    useEffect(() => {
        const st = s.current
        if (!st.dragging && !st.animFrame && itemsRef.current) {
            st.ty = tyForVal(value)
            st.prevCenter = count + value
            itemsRef.current.style.transition = 'none'
            itemsRef.current.style.transform = `translateY(${st.ty}px)`
            styleItems(count + value)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, count])

    // Non-passive touch handlers with rAF momentum animation
    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        const c = count
        const jump = c * DRUM_H
        const DECEL = 5          // exponential decay rate
        const SNAP_VEL = 100     // px/s threshold to snap

        const recenter = (st) => {
            const ci = DRUM_CENTER - st.ty / DRUM_H
            if (ci < c) {
                st.ty -= jump
                st.startTy -= jump
                s.current.prevCenter = DRUM_CENTER - st.ty / DRUM_H
            } else if (ci >= c * 2) {
                st.ty += jump
                st.startTy += jump
                s.current.prevCenter = DRUM_CENTER - st.ty / DRUM_H
            }
        }

        const applyFrame = (st) => {
            if (itemsRef.current) {
                itemsRef.current.style.transform = `translateY(${st.ty}px)`
            }
            styleItems(DRUM_CENTER - st.ty / DRUM_H)
        }

        const snapTo = (st) => {
            const rawIdx = Math.round(DRUM_CENTER - st.ty / DRUM_H)
            const val = ((rawIdx % c) + c) % c
            const targetTy = tyForVal(val)

            // Animate final snap with rAF ease-out (small distance)
            const startTy = st.ty
            const dist = targetTy - startTy
            const duration = 200
            const startTime = performance.now()

            const snapAnim = (now) => {
                const elapsed = now - startTime
                const progress = Math.min(elapsed / duration, 1)
                // ease-out quad
                const eased = 1 - (1 - progress) * (1 - progress)
                st.ty = startTy + dist * eased
                applyFrame(st)
                if (progress < 1) {
                    st.animFrame = requestAnimationFrame(snapAnim)
                } else {
                    st.animFrame = null
                    st.ty = targetTy
                    applyFrame(st)
                    if (val !== valueRef.current) onChangeRef.current(val)
                }
            }

            if (Math.abs(dist) < 1) {
                st.ty = targetTy
                applyFrame(st)
                if (val !== valueRef.current) onChangeRef.current(val)
            } else {
                st.animFrame = requestAnimationFrame(snapAnim)
            }
        }

        const onStart = (e) => {
            if (editingRef.current) return
            const st = s.current
            if (st.animFrame) {
                cancelAnimationFrame(st.animFrame)
                st.animFrame = null
            }
            if (itemsRef.current) {
                const matrix = new DOMMatrix(getComputedStyle(itemsRef.current).transform)
                st.ty = matrix.m42 || tyForVal(valueRef.current)
                itemsRef.current.style.transition = 'none'
            }
            const touch = e.touches[0]
            st.startY = touch.clientY
            st.startTy = st.ty
            st.lastY = touch.clientY
            st.lastT = Date.now()
            st.vel = 0
            st.dragging = true
        }

        const onMove = (e) => {
            const st = s.current
            if (!st.dragging) return
            e.preventDefault()
            const y = e.touches[0].clientY
            const now = Date.now()
            const dt = now - st.lastT
            if (dt > 0) st.vel = (y - st.lastY) / dt
            st.lastY = y
            st.lastT = now

            st.ty = st.startTy + (y - st.startY)
            recenter(st)
            applyFrame(st)
        }

        const onEnd = () => {
            const st = s.current
            if (!st.dragging) return
            st.dragging = false

            // Tap detection: minimal movement → open numeric input
            const totalMove = Math.abs(st.lastY - st.startY)
            if (totalMove < 5) {
                st.ty = tyForVal(valueRef.current)
                if (itemsRef.current) {
                    itemsRef.current.style.transform = `translateY(${st.ty}px)`
                }
                setEditing(true)
                setDraft(pad(valueRef.current))
                return
            }

            // Momentum animation frame by frame
            let vel = st.vel * 1000 // convert to px/s
            let lastTime = performance.now()

            const coastFrame = (now) => {
                const dt = (now - lastTime) / 1000
                lastTime = now
                vel *= Math.exp(-DECEL * dt)
                st.ty += vel * dt
                recenter(st)
                applyFrame(st)

                if (Math.abs(vel) > SNAP_VEL) {
                    st.animFrame = requestAnimationFrame(coastFrame)
                } else {
                    st.animFrame = null
                    snapTo(st)
                }
            }

            if (Math.abs(vel) > SNAP_VEL) {
                st.animFrame = requestAnimationFrame(coastFrame)
            } else {
                snapTo(st)
            }
        }

        el.addEventListener('touchstart', onStart, { passive: true })
        el.addEventListener('touchmove', onMove, { passive: false })
        el.addEventListener('touchend', onEnd, { passive: true })
        return () => {
            if (s.current.animFrame) cancelAnimationFrame(s.current.animFrame)
            el.removeEventListener('touchstart', onStart)
            el.removeEventListener('touchmove', onMove)
            el.removeEventListener('touchend', onEnd)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [max, styleItems])

    // Render 3 copies for seamless infinite loop
    const totalItems = count * 3
    const centerInTriple = count + value

    return (
        <div
            ref={containerRef}
            className="relative overflow-hidden select-none"
            style={{ height: DRUM_H * DRUM_VISIBLE, width: 88, touchAction: 'none' }}
        >
            {/* Highlight band */}
            <div
                className="absolute inset-x-0 rounded-xl pointer-events-none"
                style={{
                    top: DRUM_CENTER * DRUM_H,
                    height: DRUM_H,
                    background: 'rgba(99, 102, 241, 0.08)',
                    borderTop: '2px solid rgba(99, 102, 241, 0.2)',
                    borderBottom: '2px solid rgba(99, 102, 241, 0.2)',
                }}
            />
            {/* Scrollable items (triple for loop) */}
            <div
                ref={itemsRef}
                style={{ transform: `translateY(${tyForVal(value)}px)` }}
            >
                {Array.from({ length: totalItems }, (_, i) => {
                    const displayVal = i % count
                    const dist = Math.abs(i - centerInTriple)
                    return (
                        <div key={i} className="flex items-center justify-center" style={{ height: DRUM_H }}>
                            <span
                                className="font-semibold tabular-nums text-gray-800 dark:text-gray-100"
                                style={{ fontSize: getFontSize(dist) + 'rem', opacity: getOpacity(dist) }}
                            >
                                {pad(displayVal)}
                            </span>
                        </div>
                    )
                })}
            </div>
            {/* Fade edges */}
            <div className="absolute inset-x-0 top-0 pointer-events-none z-10 bg-gradient-to-b from-white dark:from-gray-800 to-transparent" style={{ height: DRUM_H }} />
            <div className="absolute inset-x-0 bottom-0 pointer-events-none z-10 bg-gradient-to-t from-white dark:from-gray-800 to-transparent" style={{ height: DRUM_H }} />
            {/* Numeric input overlay on tap */}
            {editing && (
                <div
                    className="absolute inset-0 flex items-center justify-center z-20 bg-white dark:bg-gray-800"
                    onTouchStart={(e) => e.stopPropagation()}
                >
                    <input
                        ref={editInputRef}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={2}
                        value={draft}
                        className="w-16 text-center text-4xl font-semibold tabular-nums outline-none bg-transparent border-b-2 border-indigo-500 text-gray-800 dark:text-gray-100"
                        onChange={(e) => setDraft(e.target.value.replace(/\D/g, '').slice(0, 2))}
                        onBlur={commitEdit}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur() }}
                        autoFocus
                    />
                </div>
            )}
        </div>
    )
}

/* ─── Main component ─── */

const WheelTimePicker = ({ value, onChange, disabled, mobile }) => {
    const hour = value instanceof Date ? value.getHours() : 0
    const minute = value instanceof Date ? value.getMinutes() : 0

    const [editingField, setEditingField] = useState(null)
    const [draft, setDraft] = useState('')
    const inputRef = useRef()
    const hourColRef = useRef()
    const minuteColRef = useRef()

    const applyTime = useCallback((h, m) => {
        if (!(value instanceof Date)) return
        const updated = new Date(value)
        updated.setHours(h)
        updated.setMinutes(m)
        updated.setSeconds(0)
        onChange?.(updated)
    }, [value, onChange])

    // Refs for wheel handlers (must be before conditional return)
    const incrementRef = useRef(null)
    const decrementRef = useRef(null)
    incrementRef.current = (field) => {
        if (field === 'hour') applyTime((hour + 1) % 24, minute)
        else applyTime(hour, (minute + 1) % 60)
    }
    decrementRef.current = (field) => {
        if (field === 'hour') applyTime((hour + 23) % 24, minute)
        else applyTime(hour, (minute + 59) % 60)
    }

    // Attach non-passive wheel listeners (desktop only)
    useEffect(() => {
        if (mobile) return
        const handler = (field) => (e) => {
            e.preventDefault()
            if (e.deltaY < 0) incrementRef.current(field)
            else decrementRef.current(field)
        }
        const hourHandler = handler('hour')
        const minuteHandler = handler('minute')
        const hourEl = hourColRef.current
        const minuteEl = minuteColRef.current
        hourEl?.addEventListener('wheel', hourHandler, { passive: false })
        minuteEl?.addEventListener('wheel', minuteHandler, { passive: false })
        return () => {
            hourEl?.removeEventListener('wheel', hourHandler)
            minuteEl?.removeEventListener('wheel', minuteHandler)
        }
    }, [mobile])

    /* ── Mobile drum mode ── */
    if (mobile) {
        return (
            <div className={`flex items-center justify-center gap-3 py-4 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
                <DrumColumn value={hour} max={23} onChange={(h) => applyTime(h, minute)} />
                <span className="text-4xl font-light text-gray-300 dark:text-gray-600 select-none">:</span>
                <DrumColumn value={minute} max={59} onChange={(m) => applyTime(hour, m)} />
            </div>
        )
    }

    /* ── Desktop compact mode ── */
    const increment = incrementRef.current
    const decrement = decrementRef.current

    const startEditing = (field) => {
        setEditingField(field)
        setDraft(pad(field === 'hour' ? hour : minute))
        setTimeout(() => inputRef.current?.select(), 0)
    }

    const commitEdit = () => {
        if (!editingField) return
        const num = parseInt(draft, 10)
        const max = editingField === 'hour' ? 23 : 59
        if (!isNaN(num)) {
            const clamped = clamp(num, 0, max)
            if (editingField === 'hour') applyTime(clamped, minute)
            else applyTime(hour, clamped)
        }
        setEditingField(null)
    }

    const renderColumn = (field, val) => {
        const isEditing = editingField === field
        const colRef = field === 'hour' ? hourColRef : minuteColRef

        return (
            <div className="flex flex-col items-center" ref={colRef}>
                <button
                    type="button"
                    className="p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    onClick={() => increment(field)}
                    disabled={disabled}
                    tabIndex={-1}
                >
                    <HiChevronUp className="text-lg" />
                </button>

                {isEditing ? (
                    <input
                        ref={inputRef}
                        type="text"
                        inputMode="numeric"
                        maxLength={2}
                        value={draft}
                        className="w-10 h-9 text-center text-xl font-bold border-b-2 border-indigo-500 outline-none bg-transparent rounded-none"
                        onChange={(e) => setDraft(e.target.value.replace(/\D/g, '').slice(0, 2))}
                        onBlur={commitEdit}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEdit()
                            if (e.key === 'Escape') setEditingField(null)
                            if (e.key === 'ArrowUp') { e.preventDefault(); increment(field) }
                            if (e.key === 'ArrowDown') { e.preventDefault(); decrement(field) }
                        }}
                        autoFocus
                    />
                ) : (
                    <button
                        type="button"
                        className="w-10 h-9 text-xl font-bold text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-text transition-colors"
                        onClick={() => !disabled && startEditing(field)}
                        disabled={disabled}
                    >
                        {pad(val)}
                    </button>
                )}

                <button
                    type="button"
                    className="p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                    onClick={() => decrement(field)}
                    disabled={disabled}
                    tabIndex={-1}
                >
                    <HiChevronDown className="text-lg" />
                </button>
            </div>
        )
    }

    return (
        <div className={`flex items-center justify-center gap-1 py-2 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
            {renderColumn('hour', hour)}
            <span className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-px">:</span>
            {renderColumn('minute', minute)}
        </div>
    )
}

export default WheelTimePicker
